import { Iconify } from '@/components/iconify';
import { FormContainerRHF } from '@/components/ui/form';
import { InputFileRHF } from '@/components/ui/form/input/file';
import { TextFieldRHF } from '@/components/ui/form/input/textField';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useAppStore } from '@/shared/stores/app';
import Logo from '@assets/logo-cargill-center.svg';
import { DownloadRounded, VerifiedUserRounded, VisibilityOffRounded, VisibilityRounded } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, AlertTitle, Button, Chip, Container, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { z } from 'zod';
import { FEED_BINARY_CONFIG, FEED_BINARY_CONFIG_DEFAULT } from '../../constants';
import { ReleaseChannelErrorModal, isReleaseChannelErrorResponse } from './release-channel-modal';

export function BinaryConfig() {
  const { version } = useAppStore();
  const [data, setConfig] = useLocalStorage<IBinaryConfig>(FEED_BINARY_CONFIG, FEED_BINARY_CONFIG_DEFAULT);

  const [showPassword, setShowPassword] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [releaseChannelError, setReleaseChannelError] = useState<string | null>(null);

  async function handleSubmit(data: z.infer<typeof resolver>) {
    setDisabled(true);

    const loadingId = toast.loading('Carregando feed manager...');

    const closeRequest = await fetch('fdm://internal/close', {
      method: 'POST',
      body: JSON.stringify(data)
    });

    if (closeRequest.status !== 200 && closeRequest.status !== 202) {
      const error = await closeRequest.json();
      logger.error(error);
      toast.remove(loadingId);
      toast.error(`Erro ao fechar conexão anterior:\n${error.message}`);

      setDisabled(false);
      setConfig(() => ({
        ...data,
        error: true
      }));

      return;
    }

    const configResponse = await fetch('fdm://internal/config', {
      method: 'POST',
      body: JSON.stringify(data)
    });

    if (configResponse.status !== 200) {
      const error = await configResponse.json();
      logger.error(error);
      toast.remove(loadingId);

      if (isReleaseChannelErrorResponse(error)) {
        setReleaseChannelError(error.message || 'Canal de versão incompatível com o banco de dados.');
        setDisabled(false);
        setConfig(() => ({
          ...data,
          error: true
        }));
        return;
      }

      toast.error(`Erro ao configurar conexão:\n${error.message}`);

      setDisabled(false);
      setConfig(() => ({
        ...data,
        error: true
      }));

      return;
    }

    setConfig(() => ({
      ...data,
      error: false
    }));
  }

  return (
    <>
      <Stack
        sx={{
          width: '100%',
          height: '100dvh',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Container maxWidth="md">
          <Stack
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2
            }}
          >
            <img src={Logo} alt="Logo Feed Manager" height={52} style={{ marginBottom: 0 }} />

            <Stack>
              <Typography variant="h5" textAlign="center" gutterBottom>
                Configuração inicial
              </Typography>

              <Typography variant="body1" textAlign="center">
                Conecte o Feed Manager à sua instância do PostgreSQL.
              </Typography>
            </Stack>

            <FormContainerRHF onSubmit={handleSubmit} resolver={resolver} defaultValues={data!}>
              <Stack
                sx={{
                  gap: 2
                }}
              >
                <Stack direction="row" spacing={2}>
                  <TextFieldRHF name="pgUser" label="Usuário" />

                  <TextFieldRHF
                    name="pgPassword"
                    label="Senha"
                    type={showPassword ? 'text' : 'password'}
                    endAdornment={
                      <IconButton onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <VisibilityRounded /> : <VisibilityOffRounded />}
                      </IconButton>
                    }
                  />
                </Stack>

                <Stack direction="row" spacing={2}>
                  <TextFieldRHF name="pgHost" label="Host" />

                  <TextFieldRHF name="pgPort" label="Porta" mask="number" />
                </Stack>

                <Stack direction="row" spacing={2}>
                  <TextFieldRHF name="pgDatabase" label="Banco de dados" />

                  <TextFieldRHF name="pgParams" label="Parâmetros" />
                </Stack>

                <InputFileRHF name="pgPath" label="Caminho alternativo do PostgreSQL" />

                <Stack direction="row" spacing={2}>
                  <Alert severity="info" icon={<Iconify icon="solar:database-bold-duotone" />} sx={{ width: '100%' }}>
                    <Typography variant="body2" fontWeight={600}>
                      <AlertTitle>Instalação em servidores</AlertTitle>
                    </Typography>
                    Para usar o Feed Manager 2, instale o Feed Servidor na máquina que vai hospedar o banco de dados
                    apenas uma vez.
                    <Stack direction="row" sx={{ mt: 1 }}>
                      <Tooltip title="Baixar o Feed Servidor">
                        <Button
                          variant="soft"
                          color="info"
                          size="small"
                          sx={{ textTransform: 'none' }}
                          startIcon={<DownloadRounded />}
                          onClick={() => window.electronAPI.downloadServerInstaller()}
                        >
                          Feed Servidor
                        </Button>
                      </Tooltip>
                    </Stack>
                  </Alert>

                  <Alert
                    severity="info"
                    icon={<Iconify icon="solar:lock-keyhole-bold-duotone" />}
                    sx={{ width: '100%' }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      <AlertTitle>Para importação de BNDs</AlertTitle>
                    </Typography>
                    Fazendas com rastreabilidade precisam instalar o Java para importar BNDs.
                    <Stack direction="row" sx={{ mt: 1 }}>
                      <Tooltip title="Baixar o Java">
                        <Button
                          variant="soft"
                          color="info"
                          size="small"
                          sx={{ textTransform: 'none' }}
                          startIcon={<DownloadRounded />}
                          href="https://javadl.oracle.com/webapps/download/AutoDL?BundleId=251391_0d8f12bc927a4e2c9f8568ca567db4ee"
                        >
                          Java
                        </Button>
                      </Tooltip>
                    </Stack>
                  </Alert>
                </Stack>

                <LoadingButton type="submit" disabled={disabled} variant="contained" loading={false} size="large">
                  Confirmar
                </LoadingButton>
              </Stack>
            </FormContainerRHF>
          </Stack>
        </Container>

        <Chip
          label={`Versão: ${version}`}
          icon={<VerifiedUserRounded />}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16
          }}
        />
      </Stack>

      <ReleaseChannelErrorModal
        open={Boolean(releaseChannelError)}
        message={releaseChannelError || ''}
        onClose={() => setReleaseChannelError(null)}
      />
    </>
  );
}

export type IBinaryConfig = z.infer<typeof resolver>;

const resolver = z.object({
  pgUser: z.string().min(1, 'é obrigatório'),
  pgPassword: z.string().min(1, 'é obrigatória'),
  pgHost: z.string().min(1, 'é obrigatório'),
  pgPort: z.string().min(1, 'é obrigatória'),
  pgDatabase: z.string().min(1, 'é obrigatório'),
  pgParams: z.string().default('?'),
  pgPath: z.string().optional(),
  error: z.boolean().default(false)
});
